import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfigurationModel, MessageTemplate, PublicPortalSettings, StormStatus } from '../shared/models/configuration.model';
import {catchError, map, retry} from 'rxjs/operators';
import {HttpErrorHandler} from '../http.error.handler';
import {environment} from 'src/environments/environment';
import {BehaviorSubject, firstValueFrom, Observable} from 'rxjs';
import {JsonApiResponse} from '../shared/models/JsonApiResponse';
import {MapLayerConfiguration} from '../shared/models/map-layer.model';
import {TrackStyles} from './model/TrackStyles';
import {TrackStylesService} from './track-styles.service';
import {SettingsService} from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {

  // raw styles loaded from backend
  trackStyles = new TrackStyles();
  readonly trackStyles$ = new BehaviorSubject<TrackStyles>(this.trackStyles);

  userTrackStyles$ = new BehaviorSubject<TrackStyles>(this.trackStyles);
  areUserTrackStylesInitialized = false;
  private readonly configUrl = `${environment.services.service}`;

  // styles loaded from backend enhanced with configuration in browser storage
  private readonly configurationModel = new BehaviorSubject<ConfigurationModel>(null);
  sharedConfigurationModel: Observable<ConfigurationModel> = this.configurationModel;

  constructor(
    private readonly http: HttpClient,
    private readonly trackStylesService: TrackStylesService,
    private readonly settingsService: SettingsService,
  ) {
  }

  initUserTrackStyles() {
    if (!this.areUserTrackStylesInitialized) {
      this.trackStyles$.subscribe(trackStyles => {
        const userTrackStyles = TrackStyles.enhanceFromSettings(trackStyles, this.settingsService);
        this.userTrackStyles$.next(userTrackStyles);
      });
      this.areUserTrackStylesInitialized = true;
    }
  }

  refreshTrackStyles() {
    this.trackStylesService.loadTrackStyles()
      .then(trackStyles => {
        this.trackStyles = trackStyles;
        this.trackStyles$.next(this.trackStyles);
      });
  }

  refreshUserTrackStyles() {
    const userTrackStyles = TrackStyles.enhanceFromSettings(this.trackStyles, this.settingsService);
    this.userTrackStyles$.next(userTrackStyles);
  }

  refreshConfiguration() {
    firstValueFrom(this.getConfiguration())
      .then(
        (response: JsonApiResponse<ConfigurationModel>) => {
          // initialize shared config model
          this.configurationModel.next(Object.assign(new ConfigurationModel(), response.data));
        }, // success path
        (error) => console.log(error)
      );
  }

  clearConfiguration() {
    this.configurationModel.next(null);
  }

  getAllMapLayers(): Promise<MapLayerConfiguration[]> {
    return firstValueFrom(
      this.http.get<JsonApiResponse<MapLayerConfiguration[]>>(`${this.configUrl}v1/configuration/mapLayer/all`)
        .pipe(
          map(response => response.data),
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getPublicMapLayers(): Promise<MapLayerConfiguration[]> {
    return firstValueFrom(
      this.http.get<JsonApiResponse<MapLayerConfiguration[]>>(`${this.configUrl}v1/configuration/mapLayer/public`)
        .pipe(
          map(response => response.data),
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getMapLayersAvailableForPublic(): Promise<MapLayerConfiguration[]> {
    return firstValueFrom(
      this.http.get<JsonApiResponse<MapLayerConfiguration[]>>(`${this.configUrl}v1/configuration/mapLayer/availableForPublic`)
        .pipe(
          map(response => response.data),
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  updateMapLayers(mapLayers: MapLayerConfiguration[]): Promise<MapLayerConfiguration[]> {
    const requestUrl = `${environment.services.service}v1/configuration/mapLayer/all`;
    return firstValueFrom(this.http.post<JsonApiResponse<MapLayerConfiguration[]>>(requestUrl, mapLayers).pipe(
      map(response => response.data),
      catchError(HttpErrorHandler.handleError) // then handle the error
    ));
  }

  updatePublicMapLayers(mapLayers: MapLayerConfiguration[]): Promise<MapLayerConfiguration[]> {
    const requestUrl = `${environment.services.service}v1/configuration/mapLayer/public`;
    return firstValueFrom(this.http.post<JsonApiResponse<MapLayerConfiguration[]>>(requestUrl, mapLayers).pipe(
      map(response => response.data),
      catchError(HttpErrorHandler.handleError) // then handle the error
    ));
  }

  updateRouteMapLayer(routeConfigurationId: number): Promise<JsonApiResponse<MapLayerConfiguration>> {
    const requestUrl = `${environment.services.service}v1/configuration/mapLayer/routeConfiguration/${routeConfigurationId}`;
    return firstValueFrom(this.http.post<JsonApiResponse<MapLayerConfiguration>>(requestUrl, {}).pipe(
      catchError(HttpErrorHandler.handleError) // then handle the error
    ));
  }

  getCustomerId(): Promise<JsonApiResponse<string>> {
    const requestUrl = `${environment.services.service}v1/configuration/customerId`;

    return firstValueFrom(
      this.http.get<JsonApiResponse<string>>(requestUrl)
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getPublicPortalSettings(): Promise<JsonApiResponse<PublicPortalSettings>> {
    const requestUrl = `${environment.services.service}v1/configuration/publicPortalSettings`;

    return firstValueFrom(
      this.http.get<JsonApiResponse<PublicPortalSettings>>(requestUrl)
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  updatePublicPortalSettings(settings: PublicPortalSettings) {
    const requestUrl = `${environment.services.service}v1/configuration/publicPortalSettings`;
    return firstValueFrom(this.http.post(requestUrl, settings).pipe(
      catchError(HttpErrorHandler.handleError) // then handle the error
    ));
  }

  getLogo(): Promise<JsonApiResponse<string>> {
    const requestUrl = `${environment.services.service}v1/configuration/logo`;

    return firstValueFrom(
      this.http.get<JsonApiResponse<string>>(requestUrl)
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  updateLogo(logo: string) {
    const requestUrl = `${environment.services.service}v1/configuration/logo`;
    return firstValueFrom(
      this.http.post(requestUrl, logo).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      )
    );
  }

  resetContent(cutoffTimestamp: Date) {
    let params = new HttpParams();
    if (cutoffTimestamp) {
      params = params.set('value', cutoffTimestamp.toISOString());
    }
    const requestUrl = `${environment.services.service}v1/configuration/contentVisibilityCutoff`;
    return firstValueFrom(
      this.http.post(requestUrl, {}, {params}).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      )
    );
  }

  updateTrackDurationHours(trackDurationHours: number) {
    let params = new HttpParams();
    params = params.set('value', trackDurationHours);
    const requestUrl = `${environment.services.service}v1/configuration/trackDurationHours`;
    return firstValueFrom(
      this.http.post(requestUrl, {}, {params}).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      )
    );
  }

  updateMessageTemplates(messageTemplates: MessageTemplate[]) {
    const requestUrl = `${environment.services.service}v1/configuration/messageTemplates`;
    return firstValueFrom(
      this.http.post(requestUrl, messageTemplates).pipe(
        catchError(HttpErrorHandler.handleError)
      )
    );
  }

  updateStormStatus(stormStatus: StormStatus) {
    const payload = {
      active: stormStatus.active,
      eventCreated: stormStatus.eventCreated ? stormStatus.eventCreated.toISOString() : undefined,
    };
    const requestUrl = `${environment.services.service}v1/configuration/stormStatus`;
    return firstValueFrom(
      this.http.post(requestUrl, payload).pipe(
        catchError(HttpErrorHandler.handleError)
      )
    );
  }

  getGuestDriversSupport(): Promise<JsonApiResponse<boolean>> {
    const requestUrl = `${environment.services.service}v1/configuration/guestDrivers`;

    return firstValueFrom(
      this.http.get<JsonApiResponse<boolean>>(requestUrl)
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  updateGuestDriversSupport(allowGuestDrivers: boolean) {
    const requestUrl = `${environment.services.service}v1/configuration/guestDrivers`;
    return firstValueFrom(this.http.post(requestUrl, allowGuestDrivers).pipe(
      catchError(HttpErrorHandler.handleError) // then handle the error
    ));
  }

  private getConfiguration() {
    return this.http.get<JsonApiResponse<ConfigurationModel>>(`${this.configUrl}v1/configuration/currentTenant`)
      .pipe(
        retry(3), // retry a failed request up to 3 times
        catchError(HttpErrorHandler.handleError) // then handle the error
      );
  }

}
